/**
 * AdvancedScheduleModal — Visual calendar multi-day block/unblock
 *
 * Props:
 *   mode         "block" | "unblock"
 *   providerId   string
 *   providerName string
 *   date         "YYYY-MM-DD"  (the date that was right-clicked — pre-selected)
 *   time         "HH:MM"       (the slot that was right-clicked)
 *   onClose      () => void
 *   onApplied    () => void
 */
import React, { useState, useMemo } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Ban, Briefcase, Coffee, Check, Clock, Users, ChevronLeft, ChevronRight, X } from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────
function t2m(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }
function m2t(mins) { return `${String(Math.floor(mins / 60)).padStart(2,'0')}:${String(mins % 60).padStart(2,'0')}`; }
function fmt12(t) {
  const [h, m] = t.split(':').map(Number);
  return `${((h % 12) || 12)}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function getWeekDates(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay(); // 0=Sun
  const mon = new Date(d); mon.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(mon); dd.setDate(mon.getDate() + i);
    return dd.toISOString().slice(0, 10);
  });
}

const TIME_OPTIONS = [];
for (let h = 6; h <= 21; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_OPTIONS.push(m2t(h * 60 + m));
  }
}

const BLOCK_TYPES = [
  { value: 'unavailable', label: 'Unavailable', icon: Ban,      color: 'text-slate-600' },
  { value: 'admin',       label: 'Admin',       icon: Briefcase, color: 'text-sky-600'   },
  { value: 'meeting',     label: 'Meeting',     icon: Users,     color: 'text-purple-600' },
  { value: 'lunch',       label: 'Lunch',       icon: Coffee,    color: 'text-amber-600' },
];

// ─── Multi-Select Calendar ───────────────────────────────────
function MultiSelectCalendar({ selectedDates, onToggle, anchorDate }) {
  const anchor = new Date(anchorDate + 'T12:00:00');
  const [viewMonth, setViewMonth] = useState(anchor.getMonth());
  const [viewYear,  setViewYear]  = useState(anchor.getFullYear());

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel  = new Date(viewYear, viewMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today       = new Date().toISOString().slice(0, 10);

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div className="select-none" data-testid="multi-select-calendar">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
          <ChevronLeft className="w-4 h-4 text-slate-500" />
        </button>
        <span className="text-sm font-semibold text-slate-700">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d, i) => (
          <div key={i} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-1">
        {days.map((day, i) => {
          if (!day) return <div key={i} />;
          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isSelected  = selectedDates.has(dateStr);
          const isToday     = dateStr === today;
          const isAnchor    = dateStr === anchorDate;

          return (
            <button key={i} onClick={() => onToggle(dateStr)}
              className={`relative h-8 w-8 mx-auto rounded-lg text-xs font-medium flex items-center justify-center transition-all
                ${isSelected
                  ? 'bg-primary text-white shadow-sm shadow-primary/30 scale-105'
                  : isToday
                    ? 'bg-primary/10 text-primary font-bold ring-1 ring-primary/30'
                    : 'hover:bg-slate-100 text-slate-700'}`}
              data-testid={`cal-day-${dateStr}`}>
              {day}
              {isSelected && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-white rounded-full border border-primary" />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected count badge */}
      {selectedDates.size > 0 && (
        <div className="mt-2 text-center">
          <span className="text-[11px] text-primary font-semibold">
            {selectedDates.size} day{selectedDates.size > 1 ? 's' : ''} selected
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ──────────────────────────────────────────────
export default function AdvancedScheduleModal({ mode, providerId, providerName, date, time, onClose, onApplied }) {
  const defaultEnd = useMemo(() => {
    const mins = t2m(time || '08:00');
    return m2t(Math.min(mins + 60, t2m('17:00')));
  }, [time]);

  const [selectedDates, setSelectedDates] = useState(() => new Set([date]));
  const [startTime, setStartTime] = useState(time || '08:00');
  const [endTime,   setEndTime]   = useState(defaultEnd);
  const [blockType, setBlockType] = useState('unavailable');
  const [loading,   setLoading]   = useState(false);

  const toggleDate = (d) => {
    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  };

  // Quick day presets — add to existing selection
  const addPreset = (preset) => {
    const today = new Date().toISOString().slice(0, 10);
    if (preset === 'today')    { setSelectedDates(new Set([date])); }
    if (preset === 'tomorrow') { setSelectedDates(new Set([addDays(date, 1)])); }
    if (preset === '+3days')   { setSelectedDates(new Set([date, addDays(date,1), addDays(date,2)])); }
    if (preset === 'thisweek') { setSelectedDates(new Set(getWeekDates(date).filter(d => d >= today))); }
    if (preset === 'clear')    { setSelectedDates(new Set()); }
  };

  // Quick time presets
  const applyTimePreset = (key) => {
    if (key === 'morning')   { setStartTime('08:00'); setEndTime('12:00'); }
    if (key === 'afternoon') { setStartTime('12:00'); setEndTime('17:00'); }
    if (key === 'fullday')   { setStartTime('08:00'); setEndTime('17:00'); }
  };
  const isTimePre = (s, e) => startTime === s && endTime === e;

  // Slot count
  const slotCount = useMemo(() => {
    const tSlots = Math.max(0, Math.floor((t2m(endTime) - t2m(startTime)) / 15));
    return tSlots * selectedDates.size;
  }, [startTime, endTime, selectedDates]);

  const handleApply = async () => {
    if (selectedDates.size === 0) { toast.error('Select at least one day'); return; }
    if (t2m(endTime) <= t2m(startTime)) { toast.error('End time must be after start time'); return; }
    setLoading(true);
    try {
      await api.post('/schedule-overrides/range', {
        provider_id: providerId,
        dates: Array.from(selectedDates).sort(),
        start_time: startTime,
        end_time: endTime,
        action: mode,
        block_type: blockType,
        block_label: BLOCK_TYPES.find(b => b.value === blockType)?.label || '',
      });
      toast.success(
        `${mode === 'unblock' ? 'Unblocked' : 'Blocked'} ${slotCount} slots across ${selectedDates.size} day${selectedDates.size > 1 ? 's' : ''}`
      );
      onApplied();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setLoading(false);
    }
  };

  const isBlock = mode === 'block';
  const accentColor = isBlock ? 'text-rose-600' : 'text-emerald-600';
  const btnClass   = isBlock
    ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-emerald-600 hover:bg-emerald-700 text-white';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="advanced-schedule-modal">

        {/* Header */}
        <DialogHeader className="px-5 pt-4 pb-3 border-b bg-slate-50/60">
          <DialogTitle className="text-sm font-semibold flex items-center gap-2">
            <Clock className={`w-4 h-4 ${accentColor}`} />
            Advanced {isBlock ? 'Block' : 'Unblock'} — {providerName?.split(' ').slice(-1)[0]}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Click days to select · {fmt12(startTime)}–{fmt12(endTime)}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[80vh] overflow-y-auto">

          {/* ── CALENDAR */}
          <div>
            {/* Quick day presets */}
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {[['today','Today'],['tomorrow','Tomorrow'],['+3days','+3 Days'],['thisweek','This Week']].map(([key, label]) => (
                <button key={key} onClick={() => addPreset(key)}
                  className="px-2.5 py-1 rounded-md border text-[11px] font-medium border-slate-200 hover:border-primary hover:text-primary transition-colors"
                  data-testid={`date-preset-${key}`}>
                  {label}
                </button>
              ))}
              {selectedDates.size > 0 && (
                <button onClick={() => addPreset('clear')}
                  className="px-2 py-1 rounded-md border text-[11px] font-medium border-slate-200 text-slate-400 hover:border-rose-300 hover:text-rose-500 transition-colors flex items-center gap-1"
                  data-testid="date-clear-btn">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            <MultiSelectCalendar
              selectedDates={selectedDates}
              onToggle={toggleDate}
              anchorDate={date}
            />
          </div>

          {/* ── TIME PRESETS */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Time of Day</p>
            <div className="flex gap-2">
              {[['morning','Morning','8–12'],['afternoon','Afternoon','12–5'],['fullday','Full Day','8–5']].map(([key, label, sub]) => (
                <button key={key} onClick={() => applyTimePreset(key)}
                  className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all
                    ${(key==='morning'&&isTimePre('08:00','12:00'))||(key==='afternoon'&&isTimePre('12:00','17:00'))||(key==='fullday'&&isTimePre('08:00','17:00'))
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                  data-testid={`time-preset-${key}`}>
                  <div>{label}</div>
                  <div className="text-[9px] text-muted-foreground mt-0.5">{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ── CUSTOM TIME RANGE */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] text-muted-foreground block mb-1">Start</label>
              <select value={startTime} onChange={e => setStartTime(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="advanced-start-time">
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
              </select>
            </div>
            <div className="text-muted-foreground text-xs pb-1.5">to</div>
            <div className="flex-1">
              <label className="text-[10px] text-muted-foreground block mb-1">End</label>
              <select value={endTime} onChange={e => setEndTime(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="advanced-end-time">
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
              </select>
            </div>
          </div>

          {/* ── BLOCK TYPE */}
          {isBlock && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Block Type</p>
              <div className="grid grid-cols-4 gap-1.5">
                {BLOCK_TYPES.map(({ value, label, icon: Icon, color }) => (
                  <button key={value} onClick={() => setBlockType(value)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[10px] font-medium transition-all
                      ${blockType === value ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                    data-testid={`block-type-${value}`}>
                    <Icon className={`w-3.5 h-3.5 ${blockType === value ? 'text-primary' : color}`} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── SUMMARY + ACTIONS */}
          <div className="flex items-center justify-between pt-1 border-t">
            <div className="text-[11px] text-muted-foreground">
              {slotCount > 0 ? (
                <span>
                  <span className="font-semibold text-slate-700">{slotCount} slots</span>
                  {' · '}{selectedDates.size} day{selectedDates.size > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-amber-600">Select days + time range</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onClose}>Cancel</Button>
              <Button size="sm" className={`text-xs h-7 gap-1.5 ${btnClass}`}
                onClick={handleApply} disabled={loading || slotCount === 0}
                data-testid="advanced-apply-btn">
                <Check className="w-3.5 h-3.5" />
                {loading ? 'Applying...' : `Apply ${isBlock ? 'Block' : 'Unblock'}`}
              </Button>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
